import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for authentication on app load
  useEffect(() => {
    checkAuthentication();
  }, []);

  // Check URL fragment for session_id from Emergent Auth redirect
  useEffect(() => {
    const checkUrlFragment = async () => {
      const fragment = window.location.hash;
      const sessionMatch = fragment.match(/session_id=([^&]+)/);
      
      if (sessionMatch) {
        const sessionId = sessionMatch[1];
        try {
          await handleAuthCallback(sessionId);
          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (error) {
          console.error('Auth callback failed:', error);
        }
      }
    };

    checkUrlFragment();
  }, []);

  const checkAuthentication = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleAuthCallback = async (sessionId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/callback`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ session_id: sessionId }),
      });

      if (response.ok) {
        const { user: userData } = await response.json();
        setUser(userData);
        
        // Redirect to appropriate dashboard based on user role
        if (userData.role === 'admin') {
          window.location.href = '/admin-dashboard';
        } else if (userData.role === 'practitioner') {
          window.location.href = '/practitioner-dashboard';
        } else {
          window.location.href = '/member-dashboard';
        }
        
        return userData;
      } else {
        throw new Error('Authentication failed');
      }
    } catch (error) {
      console.error('Auth callback error:', error);
      throw error;
    }
  };

  const login = (userType = 'client') => {
    // Determine redirect URL based on user type
    let redirectUrl;
    if (userType === 'twn-hub') {
      redirectUrl = `${window.location.origin}/twn-hub-profile`;
    } else {
      redirectUrl = `${window.location.origin}/profile`;
    }

    // Redirect to Emergent Auth
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    window.location.href = authUrl;
  };

  const logout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      window.location.href = '/';
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    checkAuthentication,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;